import React, { useState, useEffect } from "react";

import { UserContainer } from "containers";
import { useParams } from "react-router-dom";
import { StoreIdParam } from "config/raven";
import Category from "domain/model/Category";
import { newsService } from "domain/service";
import PageContainer from "components/lib/PageContainer";
import { CategoryList } from "components/lib/subcategory/CategoryList";
import { Message, Confirm } from "semantic-ui-react";
import { AddForm } from "components/lib/subcategory/AddForm";
import { newsRepository } from "domain/repository";
import { useMessage } from "hooks/useMessage";
import { RavenLoader } from "components/lib/RavenLoader";
import { StoreBreadcrumbBuilder } from "utils/breadcrumb";

const Container: React.FC = () => {
  const userContainer = UserContainer.useContainer();
  const { isAgency } = userContainer;
  const { storeId } = useParams<StoreIdParam>();

  const [categories, setCategories] = useState<Category[] | null>(null);
  const [editCategoryValue, setEditCategoryValue] = useState<string | null>(
    null
  );
  const [deleteRequestedCategoryValue, setDeleteRequestedCategoryValue] =
    useState<string | null>(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const { renderMessage, setSuccessMessage, setErrorMessage } = useMessage({
    messageSize: "tiny",
  });
  const [forRendering, setForRendering] = useState({});
  const [deleting, setDeleting] = useState(false);

  const handleUpdateCategory = (
    updateCategoryValue: string,
    updateCategoryName: string
  ) => {
    return newsRepository
      .updateCategory(
        isAgency,
        storeId,
        updateCategoryValue,
        updateCategoryName
      )
      .then(() => {
        setForRendering({});
        setSuccessMessage("カテゴリーが更新されました");
      })
      .catch(() => {
        setErrorMessage("カテゴリーの更新に失敗しました");
      })
      .finally(() => {
        setEditCategoryValue(null);
      });
  };

  const handleDeleteCategoryButton = (deleteCategoryValue: string) => {
    setOpenDeleteModal(true);
    setDeleteRequestedCategoryValue(deleteCategoryValue);
  };

  const handleDeleteCategory = () => {
    if (deleteRequestedCategoryValue === null) {
      return;
    }
    setDeleting(true);
    newsRepository
      .deleteCategory(isAgency, storeId, deleteRequestedCategoryValue)
      .then(() => {
        setForRendering({});
        setSuccessMessage("カテゴリーが削除されました");
      })
      .catch(() => {
        setErrorMessage("カテゴリーの削除に失敗しました");
      })
      .finally(() => {
        setDeleteRequestedCategoryValue(null);
        setOpenDeleteModal(false);
        setDeleting(false);
      });
  };

  useEffect(() => {
    const fetchCategories = () => {
      newsService.fetchCategory(isAgency, storeId).then((res) => {
        setCategories(res);
      });
    };
    fetchCategories();
  }, [isAgency, storeId, forRendering]);

  const AddFormComponent = () => (
    <AddForm
      handleAdd={(categoryName) =>
        newsRepository
          .addNewCategory(isAgency, storeId, categoryName)
          .then(() => {
            setForRendering({});
            setSuccessMessage("カテゴリーが追加されました");
          })
          .catch(() => {
            setErrorMessage("カテゴリーの追加に失敗しました");
          })
      }
    />
  );

  if (categories === null) {
    return <RavenLoader active />;
  } else if (categories.length === 0) {
    return (
      <>
        <Message size="tiny" content="カテゴリがありません" />
        <AddFormComponent />
      </>
    );
  } else {
    return (
      <>
        <RavenLoader active={deleting} content="削除中" />
        <Confirm
          open={openDeleteModal}
          content="カテゴリーを削除してもよろしいですか？"
          onCancel={() => setOpenDeleteModal(false)}
          onConfirm={handleDeleteCategory}
        />
        {renderMessage()}
        <AddFormComponent />
        <CategoryList
          categories={categories}
          editCategoryValue={editCategoryValue}
          setEditCategoryValue={setEditCategoryValue}
          handleUpdateCategory={handleUpdateCategory}
          handleDeleteCategoryButton={handleDeleteCategoryButton}
        />
      </>
    );
  }
};

export const NewsCategory: React.FC = () => {
  const { isAgency } = UserContainer.useContainer();
  const { storeId } = useParams<StoreIdParam>();
  return (
    <PageContainer
      header="お知らせカテゴリ一覧"
      breadcrumbs={new StoreBreadcrumbBuilder(isAgency, storeId)
        .newsPage()
        .build("お知らせカテゴリ一覧")}
    >
      <Container />
    </PageContainer>
  );
};
